import { useMemo, useState } from 'react';
import { useConditionalEffect } from 'react-delta';
import { useRecoilState, useRecoilValue } from 'recoil';

import Page, { showWaitCursor } from 'components/common/Page/Page';
import { AccessLevel, DashboardQueryOrigin, QueryOrigin, QueryRequestInput } from 'common/graphqlTypes';
import { TOKEN_TYPE } from 'common/util/filter/types';
import * as K from 'common/constants';

import {
  generateFacetValuesRequest,
  generateLaunchDashboardRequests,
  LRQRequest,
} from 'hooks/useLRQ/useLRQ';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useFacetsNeeded } from 'hooks/useFacetsNeeded';
import { useRecoilCleanup } from 'hooks/useRecoilCleanup';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import { featurePermissionsState } from 'components/account/account.state';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import { isFullScreenModeState } from 'components/other/MainNavBar/MainNavBar.state';
import { facetInfoState } from 'components/search/facets.state';
import { dashboardFacetValuesRequestsState, prefilterState } from 'components/dashboards/dashboards.state';
import SearchToolbar from 'components/search/SearchToolbar';
import DashboardRequestManager, { LRQId } from 'components/dashboards/DashboardPage/DashboardRequestManager';
import DashboardGrid from 'components/dashboards/DashboardPage/DashboardGrid';
import DashboardHeader, { autoRefreshMinutesState } from 'components/dashboards/DashboardPage/DashboardHeader';
import DashboardViewGraph from 'components/dashboards/DashboardPage/DashboardViewGraph';
import {
  getDashboardFacets,
  getGraphTitle,
  isValidGraphIndex,
} from 'components/dashboards/dashboardData';
import { Dashboard, FilterConfigType, GraphConfigType, ParameterConfigType } from 'components/dashboards/types';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';

import './DashboardPage.scss';

type DashboardPageProps = {
  dashboard: Dashboard;
  dashboardName: string;
  dashboardParams: string | null;
  startTime: string;
  endTime: string;
  refetchDashboard: () => void;
  viewGraph?: string;
  dashboardFilters: FilterConfigType[];
};

/**
 * This is the page wrapper for the Dashboard page. All it does is get the dashboard config and pass it
 * off to its child components (who will do the real work).
 */
function DashboardPage({
  dashboard,
  dashboardName,
  dashboardParams,
  dashboardFilters,
  startTime,
  endTime,
  refetchDashboard,
  viewGraph,
}: DashboardPageProps): JSX.Element {

  const isDashboardFiltersEnabled = useIsLabEnabled(K.LAB.DASHBOARD_FILTERS);
  const [idList, setIdList] = useState<LRQId[]>([]);
  const [requests, setRequests] = useState<LRQRequest[]>([]);
  const isFullScreenMode = useRecoilValue(isFullScreenModeState);
  const autoRefreshMinutes = useRecoilValue(autoRefreshMinutesState);
  const queryOrigin = (isFullScreenMode && autoRefreshMinutes > 0) ? DashboardQueryOrigin.DASHBOARD_AUTO_REFRESH : DashboardQueryOrigin.DASHBOARD;
  const featurePermissions = useRecoilValue(featurePermissionsState);
  const hasWriteAccess = featurePermissions.dashboards === AccessLevel.WRITE;
  const [ facetValuesRequests, setFacetValuesRequests ] = useRecoilState(dashboardFacetValuesRequestsState);
  const facetInfo = useRecoilValue(facetInfoState);
  const prefilter = useRecoilValue(prefilterState);

  useFacetsNeeded();
  useRecoilCleanup(dashboardFacetValuesRequestsState);

  const isNewSearch = useIsNewSearch();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTimeNs, endTimeNs } = queryTimeRange;

  let graphIndex = Number.parseInt(viewGraph || '-1', 10);
  let docTitle = dashboardName;
  if (isValidGraphIndex(dashboard.config, graphIndex)) {
    const graphConfigs = dashboard.config.graphs as GraphConfigType[];
    docTitle = `${getGraphTitle(graphConfigs[graphIndex])} Graph`;
  } else {
    graphIndex = -1;
  }

  useConditionalEffect(() => {
    const dashboardParameters = dashboard.config.parameters as ParameterConfigType[];
    const facets = getDashboardFacets(dashboardFilters, dashboardParameters, facetInfo);

    if (facets.length > 0) {
      const request = generateFacetValuesRequest({
        facets,
        filter: '',
        origin: QueryOrigin.DASHBOARD,
        startTime: startTimeNs,
        endTime: endTimeNs,
      } as unknown as QueryRequestInput);
      setFacetValuesRequests([request]);
    }

    const lists = generateLaunchDashboardRequests(startTime, endTime, dashboard, graphIndex, queryOrigin, queryTimeRange.requestTime, prefilter, dashboard.config.tabName);
    showWaitCursor(true);
    setIdList(lists.items);
    setRequests(lists.requests);
  }, isNewSearch);
  const excludeTypes = useMemo(() => [ TOKEN_TYPE.COMMAND, TOKEN_TYPE.FUNCTION ], []);
  const facetValuesRequestManager = facetValuesRequests.map((request) => <LRQRequestManager key={request.key} request={request} />);

  return (
    <Page containerModifier="DashboardPage" docTitle={docTitle} type="full">
      <DashboardRequestManager requests={requests} />
      {isDashboardFiltersEnabled && (
        <SearchToolbar excludeTypes={excludeTypes} shouldRemoveQueryTermsFromFilter={false} />
      )}
      <DashboardHeader
        refetchDashboard={refetchDashboard}
        dashboardName={dashboardName}
        dashboardConfig={dashboard.config}
        params={dashboardParams || ''}
        dashboardFilters={dashboardFilters}
        graphIndex={graphIndex}
        hasWriteAccess={hasWriteAccess}
      />
      {viewGraph !== undefined
        ? <DashboardViewGraph dashboard={dashboard} idList={idList} graphIndex={graphIndex} />
        : <DashboardGrid dashboard={dashboard} idList={idList} hasWriteAccess={hasWriteAccess} />}
      {facetValuesRequestManager}
    </Page>
  );
}

export default DashboardPage;
